import { useCallback, useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { EptwHeader } from './EptwHeader'
import { EptwSidebar } from './EptwSidebar'
import type { NavigationItem } from './NavigationMenu'
import {
  FileText,
  CheckSquare,
  Key,
  ClipboardCheck,
  LayoutDashboard,
  Shield,
  Grid,
  UserCheck,
  Clock3,
} from 'lucide-react'
import { clearAuthToken, getAuthSessionInfo } from 'modules/auth/api/authService'
import { logoutAzureSession } from 'modules/auth/api/azureAuth'
import { PERMIT_STATUS_CODES } from 'modules/permit/constants/permitStatus'
import { NAV_COUNTS_REFRESH_EVENT } from 'modules/permit/utils/navCountRefresh'
import { getPersistedSelectedSiteId, SELECTED_SITE_KEY, resolveTenantId } from 'shared/utils/tenantContext'

interface LayoutState {
  user?: any
  module?: 'E-PTW' | 'ADMIN'
  site?: {
    id?: string
    name?: string
    location?: string
    description?: string
    activeUsers?: number
  }
  menus?: any[]
  roles?: any[]
  tenantName?: string
}

const ROUTE_NAME_MAP: Record<string, string> = {
  '/dashboard': 'dashboard',
  '/overall-dashboard': 'overall-dashboard',
  '/permit-request': 'permit-request',
  '/permit-index': 'permit-index',
  '/permit-open': 'open-permit',
  '/permit-close': 'close-permit',
  '/permit-approvals': 'permit-approvals',
  '/permit-delegation': 'permit-delegation',
  '/permit-audit': 'permit-audit',
  '/timesheet': 'timesheet',
}

const PERMIT_DELEGATION_ACCESS_CODES = new Set(['10001', 'ADMIN', 'MANAGER', 'VP'])
const PERMIT_DELEGATION_ACCESS_LABELS = new Set(['ADMIN', 'MANAGER', 'VICE PRESIDENT'])

const normalizeGuid = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null
  }
  const trimmed = value.trim()
  if (!trimmed) {
    return null
  }
  return /^[0-9a-fA-F-]{36}$/.test(trimmed) ? trimmed.toLowerCase() : null
}

const normalizeDisplayText = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

export function EptwLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // A permit opened via window.open() (e.g. "เปิดดู" from the Overall Dashboard in a
  // new tab) is a standalone read-only view - it has no router state to build a
  // sidebar/nav from anyway, so skip the chrome entirely rather than show a broken shell.
  const isStandaloneReadOnlyView =
    searchParams.get('entryMode') === 'display' && searchParams.get('forceReadOnly') === 'true'
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [navCounts, setNavCounts] = useState<{
    approvals: number
    open: number
    close: number
    contractorWaiting: number
    openByRole: Record<string, number>
    closeByRole: Record<string, number>
  }>({
    approvals: 0,
    open: 0,
    close: 0,
    contractorWaiting: 0,
    openByRole: {},
    closeByRole: {},
  })

  const state = useMemo(() => ((location.state as LayoutState | undefined) ?? {}), [location.state])
  const currentPath = location.pathname
  const persistedSiteId = useMemo(() => getPersistedSelectedSiteId(), [])
  const tenantId = resolveTenantId(state.site?.id, persistedSiteId)

  useEffect(() => {
    if (state.site?.id) {
      try {
        sessionStorage.setItem(SELECTED_SITE_KEY, state.site.id)
      } catch {
        // ignore storage errors
      }
    }
  }, [state.site?.id])

  const currentUserId = useMemo(() => {
    const candidates: Array<string | null | undefined> = [
      state.user?.id,
      state.user?.user_id,
      state.user?.userId,
      state.user?.emp_id,
      state.user?.empId,
      state.user?.employee_id,
      state.user?.employeeId,
    ]
    let fallback: string | null = null
    for (const candidate of candidates) {
      if (typeof candidate !== 'string') continue
      const trimmed = candidate.trim()
      if (!trimmed) continue
      const normalized = trimmed.toLowerCase()
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed)) {
        return normalized
      }
      if (!fallback) {
        fallback = normalized
      }
    }
    return fallback
  }, [state.user])

  const resolveMenuRoleIdsByRoute = useCallback((routePath: string) => {
    const normalizedRoutePath = routePath.trim().toLowerCase()
    const results = new Set<string>()

    ;(Array.isArray(state.menus) ? state.menus : []).forEach((menu: any) => {
      const candidatePath = typeof menu?.routePath === 'string' ? menu.routePath.trim().toLowerCase() : ''
      if (!candidatePath) return
      const normalizedCandidatePath = candidatePath.startsWith('/') ? candidatePath : `/${candidatePath}`
      if (normalizedCandidatePath !== normalizedRoutePath) return

      const roleId =
        normalizeGuid(menu?.roleId) ??
        normalizeGuid(menu?.role_id) ??
        normalizeGuid(menu?.id)

      if (roleId) {
        results.add(roleId)
      }
    })

    return Array.from(results)
  }, [state.menus])

  const openMenuRoleIds = useMemo(() => resolveMenuRoleIdsByRoute('/permit-open'), [resolveMenuRoleIdsByRoute])
  const closeMenuRoleIds = useMemo(() => resolveMenuRoleIdsByRoute('/permit-close'), [resolveMenuRoleIdsByRoute])

  // TODO: Re-enable nav badge counts when performance is optimized for large datasets
  // Currently disabled to avoid slow queries during data migration with millions of records
  // const fetchNavCounts = useCallback(async () => {
  //   if (!tenantId) return
  //   try {
  //     const { getNavBadgeCounts } = await import('modules/permit/api/permitService')
  //     const result = await getNavBadgeCounts({
  //       tenantId,
  //       userId: currentUserId ?? undefined,
  //       roleIds: openMenuRoleIds.length > 0 ? openMenuRoleIds : undefined,
  //     })
  //
  //     setNavCounts({
  //       approvals: Number(result.pendingApprovalsCount),
  //       open: Number(result.openPermitsCount),
  //       close: Number(result.closePermitsCount),
  //       contractorWaiting: Number(result.contractorWaitingCount),
  //       openByRole: Object.fromEntries(
  //         Object.entries(result.openPermitsByRole).map(([k, v]) => [k, Number(v)])
  //       ),
  //       closeByRole: Object.fromEntries(
  //         Object.entries(result.closePermitsByRole).map(([k, v]) => [k, Number(v)])
  //       ),
  //     })
  //   } catch (error) {
  //     console.error('[nav-counts] failed to load counts', error)
  //   }
  // }, [currentUserId, openMenuRoleIds, tenantId])

  // TODO: Re-enable when fetchNavCounts is restored
  // useEffect(() => {
  //   void fetchNavCounts()
  // }, [currentPath, fetchNavCounts])

  // TODO: Re-enable when fetchNavCounts is restored
  // useEffect(() => {
  //   const handleRefresh = () => {
  //     void fetchNavCounts()
  //   }
  //
  //   window.addEventListener(NAV_COUNTS_REFRESH_EVENT, handleRefresh)
  //   return () => {
  //     window.removeEventListener(NAV_COUNTS_REFRESH_EVENT, handleRefresh)
  //   }
  // }, [fetchNavCounts])

  const fetchContractorWaitingCount = useCallback(async () => {
    if (!tenantId) return
    try {
      const { getContractorWaitingCount } = await import('modules/permit/api/permitService')
      const count = await getContractorWaitingCount(tenantId, currentUserId ?? undefined)
      setNavCounts((prev) => ({ ...prev, contractorWaiting: Number(count) }))
    } catch (error) {
      console.error('[nav-counts] failed to load contractor waiting count', error)
    }
  }, [currentUserId, tenantId])

  useEffect(() => {
    void fetchContractorWaitingCount()
  }, [currentPath, fetchContractorWaitingCount])

  useEffect(() => {
    const handleRefresh = () => { void fetchContractorWaitingCount() }
    window.addEventListener(NAV_COUNTS_REFRESH_EVENT, handleRefresh)
    return () => { window.removeEventListener(NAV_COUNTS_REFRESH_EVENT, handleRefresh) }
  }, [fetchContractorWaitingCount])

  const canAccessPermitDelegation = useMemo(() => {
    if (!Array.isArray(state.roles)) {
      return false
    }

    return state.roles.some((role: any) => {
      if (role?.isDelegated === true) {
        return false
      }
      const code = typeof role?.code === 'string' ? role.code.trim().toUpperCase() : ''
      const displayName = typeof role?.displayName === 'string' ? role.displayName.trim().toUpperCase() : ''
      return PERMIT_DELEGATION_ACCESS_CODES.has(code) || PERMIT_DELEGATION_ACCESS_LABELS.has(displayName)
    })
  }, [state.roles])

  const buildNavFromMenus = useCallback((menus: any[] | undefined): NavigationItem[] => {
    if (!menus || menus.length === 0) return []

    const allowedRoleIds = Array.isArray(state.roles)
      ? new Set(
          state.roles
            .map((r: any) => r?.roleId ?? r?.role_id ?? r?.id)
            .filter(Boolean)
            .map((id: any) => String(id).toLowerCase())
        )
      : new Set<string>()

    const iconMap: Record<string, any> = {
      dashboard: LayoutDashboard,
      'overall-dashboard': LayoutDashboard,
      permit: FileText,
      'permit-request': FileText,
      'permit-index': FileText,
      'permit-open': Key,
      'permit-close': ClipboardCheck,
      'permit-audit': Shield,
      'permit-approvals': CheckSquare,
      'permit-delegation': UserCheck,
      timesheet: Clock3,
      approvals: Shield,
      main: Grid,
    }

    const filteredMenus = (menus ?? [])
      .filter((m) => m?.isActive !== false)
      .filter((m) => {
        // แสดงเฉพาะเมนูที่มีการผูก roleId และอยู่ใน role ของผู้ใช้เท่านั้น
        if (allowedRoleIds.size === 0) return true
        const mid = m.roleId ?? m.role_id
        if (!mid) return false
        return allowedRoleIds.has(String(mid).toLowerCase())
      })

    if (filteredMenus.length === 0) return []

    const items = filteredMenus
      .map((m) => {
        const code = String(m.code ?? m.menuId ?? '').toLowerCase()
        const Icon = iconMap[code] ?? FileText
        const path =
          m.routePath ??
          (m.externalUrl ? `external:${m.externalUrl}` : '')
        const normalizedPath = typeof path === 'string' ? path.toLowerCase() : ''
        const normalizedRoleId = normalizeGuid(m.roleId ?? m.role_id)
        // TODO: Re-enable nav counts when performance is optimized
        // Currently badge counts are disabled during data migration, except contractor waiting
        const navCount =
          code === 'approve-vendor' ||
          code === 'approve-contractor' ||
          normalizedPath.includes('approve-contractor') ||
          normalizedPath.includes('approve-vendor')
            ? navCounts.contractorWaiting
            : undefined
        // const navCount =
        //   code === 'permit-approvals' || normalizedPath.includes('permit-approvals')
        //     ? navCounts.approvals
        //     : code === 'permit-open' || normalizedPath.includes('permit-open')
        //       ? (normalizedRoleId ? (navCounts.openByRole[normalizedRoleId] ?? 0) : navCounts.open)
        //       : code === 'permit-close' || normalizedPath.includes('permit-close')
        //         ? (normalizedRoleId ? (navCounts.closeByRole[normalizedRoleId] ?? 0) : navCounts.close)
        //         : undefined

        return {
          id: m.menuId ?? code,
          label: m.label ?? m.code ?? 'Menu',
          icon: Icon,
          path,
          roleId: normalizedRoleId ?? undefined,
          count: navCount,
          children: [] as NavigationItem[],
          parentId: m.parentId ?? null,
          sortOrder: typeof m.sortOrder === 'number' ? m.sortOrder : 99,
          externalUrl: m.externalUrl,
        }
      })

    // ป้องกันข้อมูลซ้ำ (ตาม menuId) โดยไม่รวม children เข้าด้วยกัน
    const uniqueById = items.reduce<
      (NavigationItem & { parentId?: string | null; sortOrder: number; externalUrl?: string | null })[]
    >((acc, curr) => {
      const exists = acc.find((i) => String(i.id) === String(curr.id))
      if (!exists) {
        acc.push(curr)
      }
      return acc
    }, [])

    const map = new Map<string, NavigationItem & { parentId?: string | null; sortOrder: number; externalUrl?: string | null }>()
    uniqueById.forEach((it) => {
      const id = String(it.id)
      map.set(id, it)
    })

    const roots: (NavigationItem & { parentId?: string | null; sortOrder: number; externalUrl?: string | null })[] = []
    uniqueById.forEach((it) => {
      const parentId = it.parentId
      if (parentId && map.has(String(parentId))) {
        map.get(String(parentId))?.children?.push(it)
      } else {
        roots.push(it)
      }
    })

    const sortItems = (arr: (NavigationItem & { sortOrder: number })[]) => {
      arr.sort((a, b) => a.sortOrder - b.sortOrder)
      arr.forEach((child) => sortItems(child.children as any))
    }
    sortItems(roots)

    const strip = (node: any): NavigationItem => ({
      id: node.id,
      label: node.label,
      icon: node.icon,
      path: node.path,
      roleId: node.roleId,
      count: node.count,
      roles: node.roles,
      children: (node.children ?? []).map(strip),
    })

    return roots.map(strip)
  }, [state.roles, navCounts.contractorWaiting])

  const navigationItems = useMemo(() => {
    const items = buildNavFromMenus(state.menus)
    const existsByPath = (list: NavigationItem[], path: string): boolean =>
      list.some((item) => {
        if (item.path === path) {
          return true
        }
        return Array.isArray(item.children) ? existsByPath(item.children, path) : false
      })

    if (!canAccessPermitDelegation) {
      return items
    }

    if (existsByPath(items, '/permit-delegation')) {
      return items
    }

    return [
      ...items,
      {
        id: 'permit-delegation-fallback',
        label: 'มอบหมายสิทธิ์การอนุมัติ',
        icon: UserCheck,
        path: '/permit-delegation',
        children: [],
      },
    ]
  }, [buildNavFromMenus, canAccessPermitDelegation, state.menus])
  const headerUser = useMemo(() => {
    const isGuid = (value?: string | null) =>
      typeof value === 'string' &&
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(value.trim())

    const roleCandidates = Array.isArray(state.roles)
      ? state.roles
          .map((r: any) => normalizeDisplayText(r?.displayName) ?? normalizeDisplayText(r?.code) ?? normalizeDisplayText(r?.roleId) ?? normalizeDisplayText(r?.id))
          .filter((value: string | null): value is string => Boolean(value))
          .filter((value: string) => !isGuid(value))
      : []

    const uniqueRoleCandidates = Array.from(
      new Map(
        roleCandidates.map((value) => [value.toLowerCase(), value]),
      ).values(),
    )

    const fallbackRole =
      typeof state.user?.role === 'string' && state.user.role.trim().length > 0 && !isGuid(state.user.role)
        ? state.user.role
        : undefined

    const roleLabel =
      uniqueRoleCandidates.length > 0
        ? uniqueRoleCandidates.join(', ')
        : fallbackRole ??
          (state.user?.userType === 'contractor' ? 'Contractor' : state.user ? 'Employee' : undefined)

    const name = state.user?.fullName ?? state.user?.name ?? state.user?.username
    return {
      ...state.user,
      name,
      roleLabel,
      tenantName: state.tenantName ?? state.site?.name,
    }
  }, [state.roles, state.user, state.site, state.tenantName])

  const handleNavigate = (path: string) => {
    if (!path) return
    if (path.startsWith('external:')) {
      const url = path.replace(/^external:/, '')
      if (url) {
        window.open(url, '_blank', 'noopener')
      }
      return
    }
    const routeKey = path.startsWith('/') ? path : `/${path.replace(/^\//, '')}`
    if (routeKey.startsWith('http')) {
      window.open(routeKey, '_blank', 'noopener')
      return
    }
    const nextPath = ROUTE_NAME_MAP[routeKey] ? routeKey : path
    navigate(nextPath, { state })
  }

  const handleLogout = async () => {
    const authSession = getAuthSessionInfo()
    const isContractorAzure =
      authSession?.loginMethod === 'AZURE' &&
      authSession?.userType?.trim().toUpperCase() === 'CONTRACTOR'

    clearAuthToken()

    if (isContractorAzure) {
      try {
        await logoutAzureSession('CONTRACTOR', '/login/contractor')
        return
      } catch (error) {
        console.error('Failed to logout contractor Azure session', error)
      }
    }

    navigate('/login', { replace: true })
  }

  const handleBackToModules = () => {
    navigate('/modules', { replace: true, state })
  }

  // TODO: Re-enable when nav counts are restored
  // useEffect(() => {
  //   if (currentPath === '/dashboard' && navCounts.approvals > 0) {
  //     navigate('/permit-approvals', { state })
  //   }
  // }, [currentPath, navCounts.approvals, navigate, state])

  if (isStandaloneReadOnlyView) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="p-6">
          <Outlet />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        <EptwSidebar
          navigationItems={navigationItems}
          currentPath={currentPath}
          currentUser={headerUser}
          selectedSite={state.site}
          isCollapsed={isSidebarCollapsed}
          onNavigate={handleNavigate}
        />

        {isMobileMenuOpen ? (
          <div className="lg:hidden fixed inset-0 z-50 bg-black bg-opacity-50">
            <EptwSidebar
              navigationItems={navigationItems}
              currentPath={currentPath}
              currentUser={headerUser}
              selectedSite={state.site}
              isCollapsed={false}
              onNavigate={(path) => {
                handleNavigate(path)
                setIsMobileMenuOpen(false)
              }}
              isMobile
              onCloseMobile={() => setIsMobileMenuOpen(false)}
            />
          </div>
        ) : null}

        <div className="flex min-h-screen min-w-0 flex-1 flex-col">
          <EptwHeader
            currentUser={headerUser}
            selectedSite={state.site}
            onToggleMobileMenu={() => setIsMobileMenuOpen((prev) => !prev)}
            onToggleSidebar={() => setIsSidebarCollapsed((prev) => !prev)}
            isSidebarCollapsed={isSidebarCollapsed}
            onBackToModules={handleBackToModules}
            onLogout={handleLogout}
          />

          <div className="min-w-0 flex-1 p-6">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  )
}
